import { JsonObject, JsonProperty } from 'json2typescript';
import { Moment } from 'moment';
import * as uuid from 'uuid';

import { Agent } from './agent';
import { BooleanInverterAdapter } from '../../client/helpers/serializer/adapters/boolean-inverter.adapter';
import {
    ClientInterface,
    ClientRelation,
    ClientStatus,
    ClientType,
    DocumentType,
    GenderType,
    LegalGuardianType,
    MaritalStatusType,
    MifidCategoryCode,
    Nature,
    PBCInterface,
    ProductInterface,
    ShareHolderType,
} from '../../../core';
import { ClientRelationAdapter } from '../../client/helpers/serializer/adapters/client-relation.adapter';
import { ClientStatusAdapter } from '../../client/helpers/serializer/adapters/client-status.adapter';
import { ClientTypeAdapter } from '../../client/helpers/serializer/adapters/client-type.adapter';
import { DateAdapter } from '../../client/helpers/serializer/adapters/date.adapter';
import { DocumentTypeAdapter } from '../../client/helpers/serializer/adapters/document-type.adapter';
import { GenderTypeAdapter } from '../../client/helpers/serializer/adapters/gender-type.adapters';
import { Id } from './id';
import { IdAdapter } from '../../client/helpers/serializer/adapters/id.adapter';
import { LegalGuardianTypeAdapter } from '../../client/helpers/serializer/adapters/legal-guardian-type.adapter';
import { MaritalStatusAdapter } from '../../client/helpers/serializer/adapters/marital-status-type.adapter';
import { MifidCategoryCodeAdapter } from '../../client/helpers/serializer/adapters/mifid-category-code.adapter';
import { NatureAdapter } from '../../client/helpers/serializer/adapters/nature.adapter';
import { PBC } from './pbc';
import { ShareHolder } from './share-holder';
import { StringAdapter } from '../../client/helpers/serializer/adapters/string.adapter';
import { StringToArrayAdapter } from '../../client/helpers/serializer/adapters/string-to-array.adapter';

@JsonObject('MainShareHolder')
class MainShareHolder {
    @JsonProperty('ExistsMainShareHolders', Boolean, true)
    private _hasMainShareHolders: boolean = undefined;

    @JsonProperty('ShareHolders', [ShareHolder], true)
    private _shareHolders: ShareHolder[] = [];

    private _hasSecondaryShareHolders = undefined;

    get hasMainShareHolders(): boolean {
        return this._hasMainShareHolders;
    }

    set hasMainShareHolders(value: boolean) {
        this._shareHolders = [];
        this._hasMainShareHolders = value;

        if (this._hasMainShareHolders) {
            this.hasSecondaryShareHolders = false;
        }
    }

    get hasSecondaryShareHolders(): boolean {
        if (this._hasSecondaryShareHolders === undefined) {
            this._hasSecondaryShareHolders = false;

            this.shareHolders.map((shareHolder) => {
                if (shareHolder.type === ShareHolderType.CONTROLLER) {
                    this._hasSecondaryShareHolders = true;
                }
            });
        }

        return this._hasSecondaryShareHolders;
    }

    set hasSecondaryShareHolders(value: boolean) {
        this._shareHolders = [];
        this._hasSecondaryShareHolders = value;
    }

    get shareHolders(): ShareHolder[] {
        if (!this._shareHolders) {
            this._shareHolders = [];
        }

        return this._shareHolders;
    }

    addShareHolder(value: ShareHolder) {
        if (!this.shareHolders.find((it) => it.id.value === value.id.value)) {
            this.shareHolders.push(value);
        }
    }

    updateShareHolder(value: ShareHolder) {
        this.shareHolders.map((it, idx) => {
            if (it.id.value === value.id.value) {
                this.shareHolders[idx] = value;
            }
        });
    }

    removeShareHolder(value: ShareHolder) {
        this.shareHolders.map((it, idx) => {
            if (it.id.value === value.id.value) {
                this.shareHolders.splice(idx, 1);
            }
        });
    }
}

@JsonObject('Client')
export class Client implements ClientInterface {
    @JsonProperty('Id', IdAdapter, true)
    private _id: Id = undefined;

    private readonly _internalId: Id = undefined;

    @JsonProperty('ClientCode', IdAdapter, true)
    private _code: Id = undefined;

    @JsonProperty('Code', IdAdapter, true)
    private _encryptedCode: Id = undefined;

    @JsonProperty('Type', ClientTypeAdapter, true)
    private _type: ClientType = undefined;

    private _relation: ClientRelation = undefined;

    @JsonProperty('Nature', NatureAdapter, true)
    private _nature: Nature = undefined;

    @JsonProperty('DocType', DocumentTypeAdapter, true)
    private _docType: DocumentType = undefined;

    @JsonProperty('DocId', StringAdapter, true)
    private _docId: string = undefined;

    @JsonProperty('DocExpirationDate', DateAdapter, true)
    private _docExpirationDate: Moment = undefined;

    @JsonProperty('DocExpirationDatePermanent', Boolean, true)
    private _docExpirationDatePermanent: boolean = undefined;

    @JsonProperty('Name', StringAdapter, true)
    private _name: string = undefined;

    @JsonProperty('Surname', StringAdapter, true)
    private _surname: string = undefined;

    @JsonProperty('Phone', StringAdapter, true)
    private _phone: string = undefined;

    private _phoneConfirm: string = undefined;

    private _phoneExonerate: boolean = undefined;

    @JsonProperty('Email', StringAdapter, true)
    private _email: string = undefined;

    private _emailConfirm: string = undefined;

    private _emailExonerate: boolean = undefined;

    @JsonProperty('AcceptMarketing', Boolean, true)
    private _acceptMarketing: boolean = undefined;

    @JsonProperty('AcceptEmailMarketing', Boolean, true)
    private _acceptEmailMarketing: boolean = undefined;

    @JsonProperty('DisclaimerOnboardingChecked', Boolean, true)
    private _acceptLegal: boolean = undefined;

    @JsonProperty('RegistredCountry', StringAdapter, true)
    private _country: string = undefined;

    @JsonProperty('Citizenship', StringAdapter, true)
    private _citizenship: string = undefined;

    @JsonProperty('CountryOfResidence', StringAdapter, true)
    private _countryOfResidence: string = undefined;

    @JsonProperty('LegalGuardians', [Client], true)
    private _legalGuardians: Client[] = undefined;

    @JsonProperty('BirthDate', DateAdapter, true)
    private _birthDate: Moment = undefined;

    @JsonProperty('Agent', Agent, true)
    private _agent: Agent = undefined;

    @JsonProperty('IsResident', Boolean, true)
    private _isResident: boolean = undefined;

    @JsonProperty('IsForeigner', BooleanInverterAdapter, true)
    private _isSpanish: boolean = undefined;

    private _isSoleHolder = false;

    @JsonProperty('Owner', Boolean, true)
    private _owner: boolean = undefined;

    @JsonProperty('LegalGuardianType', LegalGuardianTypeAdapter, true)
    private _legalGuardianType: LegalGuardianType = undefined;

    @JsonProperty('LegalGuardian_contract_relation', ClientRelationAdapter, true)
    private _legalGuardianRelation: ClientRelation = undefined;

    @JsonProperty('Mifi2CategoryCode', MifidCategoryCodeAdapter, true)
    private _mifidCategoryCode: MifidCategoryCode = undefined;

    @JsonProperty('Mifid_competence', IdAdapter, true)
    private _mifidCompetenceId: Id = undefined;

    @JsonProperty('Mifid_competenceName', StringAdapter, true)
    private _mifidCompetenceName: string = undefined;

    @JsonProperty('ProfileId', IdAdapter, true)
    private _profileId: Id = undefined;

    @JsonProperty('ProfileName', StringAdapter, true)
    private _profileName: string = undefined;

    @JsonProperty('ProfileIdPendingSignature', IdAdapter, true)
    private _profileIdPendingSignature: Id = undefined;

    @JsonProperty('ConvenienceGroups', StringToArrayAdapter, true)
    private _convenienceGroups: number[] = undefined;

    @JsonProperty('OnboardingStatus', ClientStatusAdapter, true)
    private _status: ClientStatus = undefined;

    @JsonProperty('Gender', GenderTypeAdapter, true)
    private _gender: GenderType = undefined;

    @JsonProperty('LandLine', StringAdapter, true)
    private _landLine: string = undefined;

    @JsonProperty('MaritalStatus', MaritalStatusAdapter, true)
    private _maritalStatus: MaritalStatusType = undefined;

    @JsonProperty('LegalEntityId', StringAdapter, true)
    private _legalEntityId: string = undefined;

    @JsonProperty('ExpirationDateLegalEntityId', DateAdapter, true)
    private _legalEntityIdExpirationDate: Moment = undefined;

    @JsonProperty('PBCData', PBC, true)
    private _pbc?: PBCInterface = undefined;

    @JsonProperty('MainShareHoldersData', MainShareHolder, true)
    private _mainShareHolder: MainShareHolder = undefined;

    private _disabled = false;
    private _preload = false;
    private _recaptchaToken: string = undefined;

    constructor() {
        this._internalId = Id.fromString(uuid.v4());
        this._name = '';
        this._surname = '';
        this.type = ClientType.NATURAL_PERSON;
        this.acceptMarketing = false;
        this.acceptEmailMarketing = false;
        this.acceptLegal = false;
        this.isSpanish = true;
        this.isResident = true;
        this.docType = this.allowedDocTypes[0];
        this.relation = ClientRelation.HOLDER;
        this.gender = this.allowedGenderTypes[0];
        this.maritalStatus = this.allowedMaritalStatusTypes[0];
        this._mainShareHolder = new MainShareHolder();
    }

    get id(): Id {
        if (!this._id) {
            return this._internalId;
        }

        return this._id;
    }

    get code(): Id {
        return this._code;
    }

    get encryptedCode(): Id {
        return this._encryptedCode;
    }

    get type(): ClientType {
        return this._type;
    }

    set type(value: ClientType) {
        const changed = this._type !== value;
        this._type = value;

        if (this._type === ClientType.NATURAL_PERSON) {
            this._legalGuardians = [];
        }

        if (this._type === ClientType.MINOR) {
            this.docExpirationDatePermanent = false;
            this.maritalStatus = MaritalStatusType.SINGLE;
        }

        if (this._type === ClientType.LEGAL_PERSON) {
            this.docExpirationDate = undefined;
            this.docExpirationDatePermanent = true;
            this.isSpanish = true;
            this.isResident = true;
        }

        if (changed) {
            this.docType = this.allowedDocTypes[0];
        }
    }

    get isLegalPerson(): boolean {
        return this._type === ClientType.LEGAL_PERSON;
    }

    get relation(): ClientRelation {
        if (!this._relation) {
            this._relation = this.legalGuardianRelation;
        }

        return this._relation;
    }

    set relation(value: ClientRelation) {
        this._relation = value;
        this._legalGuardianRelation = value;

        if (this._relation === ClientRelation.AUTHORIZED) {
            this.type = ClientType.NATURAL_PERSON;
        }
    }

    get nature(): Nature {
        return this._nature;
    }

    set nature(value: Nature) {
        this._nature = value;
    }

    get docType(): DocumentType {
        return this._docType;
    }

    set docType(value: DocumentType) {
        this._docType = value;
    }

    get allowedDocTypes(): DocumentType[] {
        if (this.type === ClientType.LEGAL_PERSON) {
            return [DocumentType.CIF];
        } else if (this.isSpanish && this.isResident) {
            return [DocumentType.DNI];
        } else if (this.isSpanish && !this.isResident) {
            return [DocumentType.DNI, DocumentType.NIF, DocumentType.NIE_NOT_RESIDENT];
        } else if (!this.isSpanish && this.isResident) {
            return [DocumentType.NIE, DocumentType.PERMISSION, DocumentType.PASSPORT, DocumentType.PASSPORT_LATIN];
        }
        return [
            DocumentType.NIE_NOT_RESIDENT,
            DocumentType.PASSPORT,
            DocumentType.PASSPORT_LATIN,
            DocumentType.IDENTIFICATION_CARD_NOT_RESIDENT,
        ];
    }

    get docId(): string {
        return this._docId;
    }

    set docId(value: string) {
        this._docId = value ? value.toUpperCase() : value;
    }

    get docExpirationDate(): Moment {
        return this._docExpirationDate;
    }

    set docExpirationDate(value: Moment) {
        this._docExpirationDate = value;
    }

    get docExpirationDatePermanent(): boolean {
        if (this._docExpirationDatePermanent === undefined) {
            this._docExpirationDatePermanent = !!this._docExpirationDate === false;
        }

        return this._docExpirationDatePermanent;
    }

    set docExpirationDatePermanent(value: boolean) {
        this._docExpirationDatePermanent = value;

        if (this._docExpirationDatePermanent) {
            this._docExpirationDate = undefined;
        }
    }

    get name(): string {
        return this._name;
    }

    set name(value: string) {
        this._name = value;
    }

    get surname(): string {
        return this._surname;
    }

    set surname(value: string) {
        this._surname = value;
    }

    get fullName(): string {
        if (this.isLegalPerson) {
            return this.name;
        }

        return `${this.name} ${this.surname}`;
    }

    get phone(): string {
        return this._phone;
    }

    set phone(value: string) {
        this._phone = value;
    }

    get phoneConfirm(): string {
        return this._phoneConfirm;
    }

    set phoneConfirm(value: string) {
        this._phoneConfirm = value;
    }

    get phoneExonerate(): boolean {
        if (this._phoneExonerate === undefined) {
            this._phoneExonerate = !!this._phone === false;
        }

        return this._phoneExonerate;
    }

    set phoneExonerate(value: boolean) {
        this._phoneExonerate = value;

        if (this._phoneExonerate) {
            this._phone = undefined;
            this._phoneConfirm = undefined;
        }
    }

    get email(): string {
        return this._email ? this._email.toLowerCase() : this._email;
    }

    set email(value: string) {
        this._email = value;
    }

    get emailConfirm(): string {
        return this._emailConfirm ? this._emailConfirm.toLowerCase() : this._emailConfirm;
    }

    set emailConfirm(value: string) {
        this._emailConfirm = value;
    }

    get emailExonerate(): boolean {
        if (this._emailExonerate === undefined) {
            this._emailExonerate = !!this._email === false;
        }

        return this._emailExonerate;
    }

    set emailExonerate(value: boolean) {
        this._emailExonerate = value;

        if (this._emailExonerate) {
            this._email = undefined;
            this._emailConfirm = undefined;
        }
    }

    get acceptMarketing(): boolean {
        return this._acceptMarketing;
    }

    set acceptMarketing(value: boolean) {
        this._acceptMarketing = value;
    }

    get acceptEmailMarketing(): boolean {
        return this._acceptEmailMarketing;
    }

    set acceptEmailMarketing(value: boolean) {
        this._acceptEmailMarketing = value;
    }

    get acceptLegal(): boolean {
        return this._acceptLegal;
    }

    set acceptLegal(value: boolean) {
        this._acceptLegal = value;
    }

    get country(): string {
        return this._country;
    }

    set country(value: string) {
        this._country = value;
    }

    get citizenship(): string {
        if (!this._citizenship && this.isSpanish) {
            this._citizenship = 'ES';
        }

        return this._citizenship;
    }

    set citizenship(value: string) {
        this._citizenship = value;
    }

    get countryOfResidence(): string {
        if (!this._countryOfResidence && this.isResident) {
            this._countryOfResidence = 'ES';
        }

        return this._countryOfResidence;
    }

    set countryOfResidence(value: string) {
        this._countryOfResidence = value;
    }

    get legalGuardians(): Client[] {
        if (!this._legalGuardians) {
            this._legalGuardians = [];
        }

        return this._legalGuardians;
    }

    public addLegalGuardian(value: Client) {
        if (!this.legalGuardians.find((it) => String(it.id) === String(value.id))) {
            this.legalGuardians.push(value);
        }
    }

    public removeLegalGuardian(value: Client) {
        this.legalGuardians.map((it, idx) => {
            if (String(it.id) === String(value.id)) {
                this.legalGuardians.splice(idx, 1);
            }
        });
    }

    get birthDate(): Moment {
        return this._birthDate;
    }

    set birthDate(value: Moment) {
        this._birthDate = value;
    }

    get agent(): Agent {
        return this._agent;
    }

    set agent(value: Agent) {
        this._agent = value;
    }

    get isResident(): boolean {
        return this._isResident;
    }

    set isResident(value: boolean) {
        this._isResident = value;

        this.docType = this.allowedDocTypes[0];
        this.docId = undefined;
    }

    get isSpanish(): boolean {
        return this._isSpanish;
    }

    set isSpanish(value: boolean) {
        this._isSpanish = value;

        this.docType = this.allowedDocTypes[0];
        this.docId = undefined;
    }

    get isSoleHolder(): boolean {
        return this._isSoleHolder;
    }

    set isSoleHolder(value: boolean) {
        this._isSoleHolder = value;
    }

    get owner(): boolean {
        return this._owner;
    }

    get legalGuardianType(): LegalGuardianType {
        return this._legalGuardianType;
    }

    set legalGuardianType(value: LegalGuardianType) {
        this._legalGuardianType = value;
    }

    get isAttorneyInFact(): boolean {
        return this._legalGuardianType === LegalGuardianType.ATTORNEY_IN_FACT;
    }

    get legalGuardianRelation(): ClientRelation {
        return this._legalGuardianRelation;
    }

    set legalGuardianRelation(value: ClientRelation) {
        this._relation = value;
        this._legalGuardianRelation = value;

        if (this._relation === ClientRelation.AUTHORIZED) {
            this.type = ClientType.NATURAL_PERSON;
        }
    }

    get mifidCategoryCode(): MifidCategoryCode {
        return this._mifidCategoryCode;
    }

    get mifidCompetenceId(): Id {
        return this._mifidCompetenceId;
    }

    set mifidCompetenceId(value: Id) {
        this._mifidCompetenceId = value;
    }

    get mifidCompetenceName(): string {
        return this._mifidCompetenceName;
    }

    set mifidCompetenceName(value: string) {
        this._mifidCompetenceName = value;
    }

    get profileId(): Id {
        if (this._profileId && String(this._profileId) === '0') {
            this._profileId = undefined;
        }

        return this._profileId;
    }

    set profileId(value: Id) {
        this._profileId = value;
    }

    get profileName(): string {
        return this._profileName;
    }

    set profileName(value: string) {
        this._profileName = value;
    }

    get profileIdPendingSignature(): Id {
        if (!this._profileIdPendingSignature) {
            this._profileIdPendingSignature = new Id('0');
        }

        return this._profileIdPendingSignature;
    }

    get convenienceGroups(): number[] {
        if (!this._convenienceGroups) {
            this._convenienceGroups = [];
        }

        return this._convenienceGroups;
    }

    get status(): ClientStatus {
        return this._status;
    }

    set status(value: ClientStatus) {
        this._status = value;
    }

    isRejected(): boolean {
        return this.status === ClientStatus.REJECTED;
    }

    get pbc(): PBCInterface {
        if (!this._pbc) {
            return new PBC();
        }

        return this._pbc;
    }

    get hasPBCPending(): boolean {
        return this.pbc.detection || this.shareHolders.some((it) => it.hasPBCPending);
    }

    get hasShareHolders(): boolean {
        return this.hasMainShareHolders;
    }

    get hasMainShareHolders(): boolean {
        return this._mainShareHolder.hasMainShareHolders;
    }

    set hasMainShareHolders(value: boolean) {
        this._mainShareHolder.hasMainShareHolders = value;
    }

    get hasSecondaryShareHolders(): boolean {
        return this._mainShareHolder.hasSecondaryShareHolders;
    }

    set hasSecondaryShareHolders(value: boolean) {
        this._mainShareHolder.hasSecondaryShareHolders = value;
    }

    get shareHolders(): ShareHolder[] {
        this._mainShareHolder.shareHolders.forEach((it) => (it.rejected = this.isRejected()));
        return this._mainShareHolder.shareHolders;
    }

    addShareHolder(value: ShareHolder) {
        this._mainShareHolder.addShareHolder(value);
    }

    updateShareHolder(value: ShareHolder) {
        this._mainShareHolder.updateShareHolder(value);
    }

    removeShareHolder(value: ShareHolder) {
        this._mainShareHolder.removeShareHolder(value);
    }

    get disabled(): boolean {
        return this._disabled;
    }

    set disabled(value: boolean) {
        this._disabled = value;
    }

    get preload(): boolean {
        return this._preload;
    }

    set preload(value: boolean) {
        this._preload = value;
    }

    get recaptchaToken(): string {
        return this._recaptchaToken;
    }

    set recaptchaToken(value: string) {
        this._recaptchaToken = value;
    }

    get gender(): GenderType {
        if (this._gender === 'E') {
            this._gender = undefined;
        }
        return this._gender;
    }

    set gender(value: GenderType) {
        this._gender = value;
    }

    get allowedGenderTypes(): GenderType[] {
        return [
            GenderType.CHOOSE,
            GenderType.WOMAN,
            GenderType.MAN,
        ];
    }

    get landLine(): string {
        return this._landLine;
    }

    set landLine(value: string) {
        this._landLine = value;
    }

    get maritalStatus(): MaritalStatusType {
        if (this._maritalStatus === 'E') {
            this._maritalStatus = undefined;
        }
        return this._maritalStatus;
    }

    set maritalStatus(value: MaritalStatusType) {
        this._maritalStatus = value;
    }

    get allowedMaritalStatusTypes(): MaritalStatusType[] {
        return [
            MaritalStatusType.CHOOSE,
            MaritalStatusType.SINGLE,
            MaritalStatusType.MARRIED,
            MaritalStatusType.OTHER,
        ];
    }


    get legalEntityId(): string {
        return this._legalEntityId;
    }

    set legalEntityId(value: string) {
        this._legalEntityId = value;
    }

    get legalEntityIdExpirationDate(): Moment {
        return this._legalEntityIdExpirationDate;
    }

    set legalEntityIdExpirationDate(value: Moment) {
        this._legalEntityIdExpirationDate = value;
    }

    copy(client: ClientInterface) {
        this._id = client.id as Id;
        this._code = client.code as Id;
        this._encryptedCode = client.encryptedCode as Id;
        this._type = client.type;
        this._docType = client.docType;
        this._nature = client.nature;
        this._docExpirationDate = client.docExpirationDate;
        this._docExpirationDatePermanent = client.docExpirationDatePermanent;
        this._name = client.name;
        this._surname = client.surname;
        this._phone = client.phone;
        this._phoneConfirm = client.phone;
        this._phoneExonerate = client.phoneExonerate;
        this._email = client.email;
        this._emailConfirm = client.email;
        this._emailExonerate = client.emailExonerate;
        this._acceptMarketing = client.acceptMarketing;
        this._acceptEmailMarketing = client.acceptEmailMarketing;
        this._acceptLegal = client.acceptLegal;
        this._emailExonerate = client.emailExonerate;
        this._country = client.country;
        this._citizenship = client.citizenship;
        this._countryOfResidence = client.countryOfResidence;
        this._legalGuardians = client.legalGuardians as Client[];
        this._birthDate = client.birthDate;
        this._agent = client.agent as Agent;
        this._isResident = client.isResident;
        this._isSpanish = client.isSpanish;
        this._status = client.status;
        this._legalGuardianType = client.legalGuardianType;
        this._mifidCompetenceId = client.mifidCompetenceId as Id;
        this._mifidCompetenceName = client.mifidCompetenceName;
        this._profileId = client.profileId as Id;
        this._profileName = client.profileName;
        this._profileIdPendingSignature = client.profileIdPendingSignature as Id;
        this._convenienceGroups = client.convenienceGroups;
        this._gender = client.gender;
        this._maritalStatus = client.maritalStatus;
        this._landLine = client.landLine;
        this._legalEntityId = client.legalEntityId;
        this._legalEntityIdExpirationDate = client.legalEntityIdExpirationDate;
    }

    isProductConvenience(product: ProductInterface): boolean {
        return product && (this.convenienceGroups.length === 0 || this.convenienceGroups.indexOf(product.convenienceGroup) !== -1);
    }
}
